import React, { useState, useEffect } from "react";
import { 
  FaSave, 
  FaPlus, 
  FaTrash, 
  FaEdit, 
  FaCheck, 
  FaTimes,
  FaDollarSign,
  FaClock,
  FaGraduationCap,
  FaBook,
  FaStar,
  FaPercentage,
  FaCog,
  FaInfoCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaToggleOn,
  FaToggleOff,
  FaArrowUp,
  FaArrowDown,
  FaFileAlt,
  FaUniversity
} from "react-icons/fa";
import api from "../api/api";
import "./AdminPricingPage.css";

const AdminPricingPage = () => {
  const [activeTab, setActiveTab] = useState('academic');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State for all pricing data
  const [siteSettings, setSiteSettings] = useState({
    currency: 'USD',
    minimum_order: 30,
    enable_rush_fees: true,
    enable_additional_services: true,
    tax_rate: 0
  });

  const [academicLevels, setAcademicLevels] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);
  const [pageDiscounts, setPageDiscounts] = useState([]);
  const [subjectMultipliers, setSubjectMultipliers] = useState([]);

  // Edit states
  const [editingAcademic, setEditingAcademic] = useState(null);
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null);

  // New item states
  const [newAcademic, setNewAcademic] = useState({ 
    academic_level: '', 
    price_per_page: '', 
    is_active: true, 
    sort_order: 0 
  });
  
  const [newDeadline, setNewDeadline] = useState({ 
    deadline_range: '', 
    rush_fee_percentage: '', 
    display_name: '', 
    is_active: true, 
    sort_order: 0 
  });
  
  const [newService, setNewService] = useState({ 
    service_type: '', 
    display_name: '', 
    description: '', 
    fee_type: 'FIXED', 
    fixed_price: '', 
    per_page_price: '', 
    percentage: '', 
    is_active: true, 
    sort_order: 0 
  });
  
  const [newDiscount, setNewDiscount] = useState({ 
    min_pages: '', 
    max_pages: '', 
    modifier_type: 'DISCOUNT', 
    modifier_value: '', 
    is_active: true 
  });
  
  const [newSubject, setNewSubject] = useState({ 
    subject: '', 
    price_multiplier: '1.00', 
    is_active: true 
  });

  // Fetch all pricing data
  useEffect(() => {
    fetchAllPricing();
  }, []);

  const fetchAllPricing = async () => {
    try {
      setLoading(true);
      const response = await api.get("/administration/pricing/");
      const data = response.data;
      console.log("Fetched pricing data:", data);
      
      // Transform academic levels
      const academicArray = Object.entries(data.academic_levels || {}).map(([level, price], index) => ({
        id: `academic-${index}-${Date.now()}`,
        academic_level: level,
        price_per_page: price,
        is_active: true,
        sort_order: index
      }));
      setAcademicLevels(academicArray);

      // Transform deadlines
      const deadlinesWithIds = (data.deadlines || []).map((item, index) => ({
        ...item,
        id: `deadline-${index}-${Date.now()}`,
        is_active: item.is_active !== undefined ? item.is_active : true
      }));
      setDeadlines(deadlinesWithIds);
      
      // Transform additional services
      const servicesWithIds = (data.additional_services || []).map((item, index) => ({
        id: item.id || `service-${index}-${Date.now()}`,
        type: item.type || '',
        name: item.name || '',
        description: item.description || '',
        fee_type: item.fee_type || 'FIXED',
        price: item.price || 0,
        price_per_page: item.price_per_page || 0,
        percentage: item.percentage || 0,
        is_active: item.is_active !== undefined ? item.is_active : true,
        sort_order: item.sort_order || index
      }));
      setAdditionalServices(servicesWithIds);
      
      // Transform page discounts
      const discountsWithIds = (data.page_discounts || []).map((item, index) => ({
        id: `discount-${index}-${Date.now()}`,
        min_pages: item.min_pages,
        max_pages: item.max_pages,
        modifier_type: item.modifier_type ? item.modifier_type.toUpperCase() : 'DISCOUNT',
        modifier_value: item.modifier_value,
        is_active: item.is_active !== undefined ? item.is_active : true
      }));
      setPageDiscounts(discountsWithIds);
      
      // Transform subject multipliers
      const subjectArray = Object.entries(data.subject_multipliers || {}).map(([subject, multiplier], index) => ({
        id: `subject-${index}-${Date.now()}`,
        subject: subject,
        price_multiplier: multiplier,
        is_active: true
      }));
      setSubjectMultipliers(subjectArray);

      // Set site settings
      if (data.site_settings) {
        setSiteSettings(data.site_settings);
      }

      setError(null);
    } catch (err) {
      console.error("Failed to fetch pricing:", err);
      setError("Failed to load pricing data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Save functions
  const saveAllHandler = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Prepare data in the exact format expected by the backend
      const pricingData = {
        academic_levels: academicLevels.map(item => ({
          academic_level: item.academic_level,
          price_per_page: parseFloat(item.price_per_page),
          is_active: item.is_active,
          sort_order: item.sort_order || 0
        })),
        
        deadlines: deadlines.map(item => ({
          deadline_range: item.range || item.deadline_range,
          rush_fee_percentage: parseFloat(item.percentage || item.rush_fee_percentage),
          display_name: item.display_name,
          is_active: item.is_active,
          sort_order: item.sort_order || 0
        })),
        
        services: additionalServices.map(item => ({
          type: item.type,
          name: item.name,
          description: item.description || '',
          fee_type: item.fee_type,
          price: item.fee_type === 'FIXED' ? parseFloat(item.price || 0) : 0,
          price_per_page: item.fee_type === 'PER_PAGE' ? parseFloat(item.price_per_page || 0) : 0,
          percentage: item.fee_type === 'PERCENTAGE' ? parseFloat(item.percentage || 0) : 0,
          is_active: item.is_active,
          sort_order: item.sort_order || 0
        })),
        
        discounts: pageDiscounts.map(item => ({
          min_pages: parseInt(item.min_pages),
          max_pages: item.max_pages ? parseInt(item.max_pages) : null,
          modifier_type: item.modifier_type,
          modifier_value: parseFloat(item.modifier_value),
          is_active: item.is_active
        })),
        
        subjects: subjectMultipliers.map(item => ({
          subject: item.subject,
          price_multiplier: parseFloat(item.price_multiplier),
          is_active: item.is_active
        })),
        
        site_settings: {
          currency: siteSettings.currency,
          minimum_order: parseFloat(siteSettings.minimum_order),
          tax_rate: parseFloat(siteSettings.tax_rate),
          enable_rush_fees: siteSettings.enable_rush_fees,
          enable_additional_services: siteSettings.enable_additional_services
        }
      };
      
      console.log("Saving pricing data:", pricingData);
      
      const response = await api.post("/administration/pricing/save-all/", pricingData);
      console.log("Save response:", response.data);
      
      setSuccess(response.data.message || "All pricing settings saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh data after save
      await fetchAllPricing();
    } catch (err) {
      console.error("Failed to save all pricing:", err);
      console.error("Error response:", err.response?.data);
      setError(err.response?.data?.error || "Failed to save all pricing data");
    } finally {
      setSaving(false);
    }
  };

  const saveSiteSettingsHandler = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await api.post("/administration/pricing/site-settings/", siteSettings);
      console.log("Save site settings response:", response.data);
      
      setSuccess(response.data.message || "Site settings saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Failed to save site settings:", err);
      setError(err.response?.data?.error || "Failed to save site settings");
    } finally {
      setSaving(false);
    }
  };

  const saveAcademicLevelHandler = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await api.post("/administration/pricing/academic-levels/", {
        academic_levels: academicLevels.map(item => ({
          academic_level: item.academic_level,
          price_per_page: parseFloat(item.price_per_page),
          is_active: item.is_active,
          sort_order: item.sort_order || 0
        }))
      });
      
      console.log("Save academic levels response:", response.data);
      
      setSuccess(response.data.message || "Academic levels saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
      setEditingAcademic(null);
    } catch (err) {
      console.error("Failed to save academic levels:", err);
      setError(err.response?.data?.error || "Failed to save academic levels");
    } finally {
      setSaving(false);
    }
  };

  const saveDeadlineHandler = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await api.post("/administration/pricing/deadlines/", {
        deadlines: deadlines.map(item => ({
          deadline_range: item.range || item.deadline_range,
          rush_fee_percentage: parseFloat(item.percentage || item.rush_fee_percentage),
          display_name: item.display_name,
          is_active: item.is_active,
          sort_order: item.sort_order || 0
        }))
      });
      
      console.log("Save deadlines response:", response.data);
      
      setSuccess(response.data.message || "Deadlines saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
      setEditingDeadline(null);
    } catch (err) {
      console.error("Failed to save deadlines:", err);
      setError(err.response?.data?.error || "Failed to save deadlines");
    } finally {
      setSaving(false);
    }
  };

  const saveServiceHandler = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await api.post("/administration/pricing/services/", {
        services: additionalServices.map(item => ({
          type: item.type,
          name: item.name,
          description: item.description || '',
          fee_type: item.fee_type,
          price: item.fee_type === 'FIXED' ? parseFloat(item.price || 0) : 0,
          price_per_page: item.fee_type === 'PER_PAGE' ? parseFloat(item.price_per_page || 0) : 0,
          percentage: item.fee_type === 'PERCENTAGE' ? parseFloat(item.percentage || 0) : 0,
          is_active: item.is_active,
          sort_order: item.sort_order || 0
        }))
      });
      
      console.log("Save services response:", response.data);
      
      setSuccess(response.data.message || "Services saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
      setEditingService(null);
    } catch (err) {
      console.error("Failed to save services:", err);
      setError(err.response?.data?.error || "Failed to save services");
    } finally {
      setSaving(false);
    }
  };

  const saveDiscountHandler = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await api.post("/administration/pricing/page-discounts/", {
        discounts: pageDiscounts.map(item => ({
          min_pages: parseInt(item.min_pages),
          max_pages: item.max_pages ? parseInt(item.max_pages) : null,
          modifier_type: item.modifier_type,
          modifier_value: parseFloat(item.modifier_value),
          is_active: item.is_active
        }))
      });
      
      console.log("Save discounts response:", response.data);
      
      setSuccess(response.data.message || "Page discounts saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
      setEditingDiscount(null);
    } catch (err) {
      console.error("Failed to save page discounts:", err);
      setError(err.response?.data?.error || "Failed to save page discounts");
    } finally {
      setSaving(false);
    }
  };

  const saveSubjectHandler = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await api.post("/administration/pricing/subject-multipliers/", {
        subjects: subjectMultipliers.map(item => ({
          subject: item.subject,
          price_multiplier: parseFloat(item.price_multiplier),
          is_active: item.is_active
        }))
      });
      
      console.log("Save subjects response:", response.data);
      
      setSuccess(response.data.message || "Subject multipliers saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
      setEditingSubject(null);
    } catch (err) {
      console.error("Failed to save subject multipliers:", err);
      setError(err.response?.data?.error || "Failed to save subject multipliers");
    } finally {
      setSaving(false);
    }
  };

  // Add new items
  const addAcademicLevel = () => {
    if (newAcademic.academic_level && newAcademic.price_per_page) {
      setAcademicLevels([
        ...academicLevels, 
        { 
          ...newAcademic, 
          id: `academic-new-${Date.now()}`,
          price_per_page: parseFloat(newAcademic.price_per_page)
        }
      ]);
      setNewAcademic({ academic_level: '', price_per_page: '', is_active: true, sort_order: academicLevels.length });
    }
  };

  const addDeadline = () => {
    if (newDeadline.deadline_range && newDeadline.rush_fee_percentage && newDeadline.display_name) {
      setDeadlines([
        ...deadlines, 
        { 
          ...newDeadline, 
          id: `deadline-new-${Date.now()}`,
          rush_fee_percentage: parseFloat(newDeadline.rush_fee_percentage)
        }
      ]);
      setNewDeadline({ deadline_range: '', rush_fee_percentage: '', display_name: '', is_active: true, sort_order: deadlines.length });
    }
  };

  const addService = () => {
    if (newService.service_type && newService.display_name) {
      const serviceData = {
        id: `service-new-${Date.now()}`,
        type: newService.service_type,
        name: newService.display_name,
        description: newService.description || '',
        fee_type: newService.fee_type,
        is_active: true,
        sort_order: additionalServices.length
      };
      
      // Add price based on fee type
      if (newService.fee_type === 'FIXED') {
        serviceData.price = parseFloat(newService.fixed_price) || 0;
      } else if (newService.fee_type === 'PER_PAGE') {
        serviceData.price_per_page = parseFloat(newService.per_page_price) || 0;
      } else if (newService.fee_type === 'PERCENTAGE') {
        serviceData.percentage = parseFloat(newService.percentage) || 0;
      }
      
      setAdditionalServices([...additionalServices, serviceData]);
      setNewService({ 
        service_type: '', 
        display_name: '', 
        description: '', 
        fee_type: 'FIXED', 
        fixed_price: '', 
        per_page_price: '', 
        percentage: '', 
        is_active: true, 
        sort_order: additionalServices.length 
      });
    }
  };

  const addDiscount = () => {
    if (newDiscount.min_pages && newDiscount.modifier_value) {
      setPageDiscounts([
        ...pageDiscounts, 
        { 
          ...newDiscount, 
          id: `discount-new-${Date.now()}`,
          min_pages: parseInt(newDiscount.min_pages),
          modifier_value: parseFloat(newDiscount.modifier_value),
          max_pages: newDiscount.max_pages ? parseInt(newDiscount.max_pages) : null
        }
      ]);
      setNewDiscount({ min_pages: '', max_pages: '', modifier_type: 'DISCOUNT', modifier_value: '', is_active: true });
    }
  };

  const addSubject = () => {
    if (newSubject.subject && newSubject.price_multiplier) {
      setSubjectMultipliers([
        ...subjectMultipliers, 
        { 
          ...newSubject, 
          id: `subject-new-${Date.now()}`,
          price_multiplier: parseFloat(newSubject.price_multiplier)
        }
      ]);
      setNewSubject({ subject: '', price_multiplier: '1.00', is_active: true });
    }
  };

  // Delete items
  const deleteAcademicLevel = (id) => {
    setAcademicLevels(academicLevels.filter(item => item.id !== id));
  };

  const deleteDeadline = (id) => {
    setDeadlines(deadlines.filter(item => item.id !== id));
  };

  const deleteService = (id) => {
    setAdditionalServices(additionalServices.filter(item => item.id !== id));
  };

  const deleteDiscount = (id) => {
    setPageDiscounts(pageDiscounts.filter(item => item.id !== id));
  };

  const deleteSubject = (id) => {
    setSubjectMultipliers(subjectMultipliers.filter(item => item.id !== id));
  };

  // Toggle active status
  const toggleAcademicActive = (id) => {
    setAcademicLevels(academicLevels.map(item => 
      item.id === id ? { ...item, is_active: !item.is_active } : item
    ));
  };

  const toggleDeadlineActive = (id) => {
    setDeadlines(deadlines.map(item => 
      item.id === id ? { ...item, is_active: !item.is_active } : item
    ));
  };

  const toggleServiceActive = (id) => {
    setAdditionalServices(additionalServices.map(item => 
      item.id === id ? { ...item, is_active: !item.is_active } : item
    ));
  };

  const toggleDiscountActive = (id) => {
    setPageDiscounts(pageDiscounts.map(item => 
      item.id === id ? { ...item, is_active: !item.is_active } : item
    ));
  };

  const toggleSubjectActive = (id) => {
    setSubjectMultipliers(subjectMultipliers.map(item => 
      item.id === id ? { ...item, is_active: !item.is_active } : item
    ));
  };

  // Move items up/down for sorting
  const moveItemUp = (array, setArray, index) => {
    if (index > 0) {
      const newArray = [...array];
      [newArray[index - 1], newArray[index]] = [newArray[index], newArray[index - 1]];
      setArray(newArray);
    }
  };

  const moveItemDown = (array, setArray, index) => {
    if (index < array.length - 1) {
      const newArray = [...array];
      [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
      setArray(newArray);
    }
  };

  // Helper to get display value for service price
  const getServicePriceDisplay = (service) => {
    if (service.fee_type === 'FIXED') {
      return `$${service.price || 0}`;
    } else if (service.fee_type === 'PER_PAGE') {
      return `$${service.price_per_page || 0}/page`;
    } else if (service.fee_type === 'PERCENTAGE') {
      return `${service.percentage || 0}%`;
    }
    return '$0';
  };

  if (loading) {
    return (
      <div className="admin-pricing-loading">
        <FaSpinner className="loading-spinner" />
        <p>Loading pricing configuration...</p>
      </div>
    );
  }

  return (
    <div className="admin-pricing-container">
      <div className="admin-pricing-header">
        <h1>Pricing Management</h1>
        <p>Configure all pricing settings for your platform</p>
      </div>

      {error && (
        <div className="admin-pricing-error">
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="admin-pricing-success">
          <FaCheck />
          <span>{success}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-pricing-tabs">
        <button 
          className={`tab-btn ${activeTab === 'academic' ? 'active' : ''}`}
          onClick={() => setActiveTab('academic')}
        >
          <FaGraduationCap /> Academic Levels
        </button>
        <button 
          className={`tab-btn ${activeTab === 'deadlines' ? 'active' : ''}`}
          onClick={() => setActiveTab('deadlines')}
        >
          <FaClock /> Deadlines
        </button>
        <button 
          className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          <FaStar /> Additional Services
        </button>
        <button 
          className={`tab-btn ${activeTab === 'discounts' ? 'active' : ''}`}
          onClick={() => setActiveTab('discounts')}
        >
          <FaPercentage /> Page Discounts
        </button>
        <button 
          className={`tab-btn ${activeTab === 'subjects' ? 'active' : ''}`}
          onClick={() => setActiveTab('subjects')}
        >
          <FaBook /> Subject Multipliers
        </button>
        <button 
          className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <FaCog /> Site Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className="admin-pricing-content">
        {/* Academic Levels Tab */}
        {activeTab === 'academic' && (
          <div className="pricing-section">
            <div className="section-header">
              <h2><FaGraduationCap /> Academic Level Pricing</h2>
              <p>Set base price per page for each academic level</p>
              <button 
                className="save-tab-btn"
                onClick={saveAcademicLevelHandler}
                disabled={saving}
              >
                {saving ? <FaSpinner className="spinning" /> : <FaSave />}
                Save Academic Levels
              </button>
            </div>

            <div className="pricing-table">
              <table>
                <thead>
                  <tr>
                    <th>Sort</th>
                    <th>Academic Level</th>
                    <th>Price per Page ($)</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {academicLevels.map((level, index) => (
                    <tr key={level.id}>
                      <td className="sort-controls">
                        <button 
                          className="sort-btn"
                          onClick={() => moveItemUp(academicLevels, setAcademicLevels, index)}
                          disabled={index === 0}
                        >
                          <FaArrowUp />
                        </button>
                        <button 
                          className="sort-btn"
                          onClick={() => moveItemDown(academicLevels, setAcademicLevels, index)}
                          disabled={index === academicLevels.length - 1}
                        >
                          <FaArrowDown />
                        </button>
                      </td>
                      <td>
                        {editingAcademic === level.id ? (
                          <input
                            type="text"
                            value={level.academic_level}
                            onChange={(e) => {
                              const newLevels = [...academicLevels];
                              newLevels[index].academic_level = e.target.value;
                              setAcademicLevels(newLevels);
                            }}
                          />
                        ) : (
                          level.academic_level
                        )}
                      </td>
                      <td>
                        {editingAcademic === level.id ? (
                          <input
                            type="number"
                            step="0.01"
                            value={level.price_per_page}
                            onChange={(e) => {
                              const newLevels = [...academicLevels];
                              newLevels[index].price_per_page = parseFloat(e.target.value);
                              setAcademicLevels(newLevels);
                            }}
                          />
                        ) : (
                          `$${level.price_per_page}`
                        )}
                      </td>
                      <td>
                        <button 
                          className={`status-toggle ${level.is_active ? 'active' : 'inactive'}`}
                          onClick={() => toggleAcademicActive(level.id)}
                        >
                          {level.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </button>
                      </td>
                      <td className="action-btns">
                        {editingAcademic === level.id ? (
                          <>
                            <button 
                              className="save-btn"
                              onClick={() => {
                                saveAcademicLevelHandler();
                                setEditingAcademic(null);
                              }}
                            >
                              <FaCheck />
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={() => setEditingAcademic(null)}
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              className="edit-btn"
                              onClick={() => setEditingAcademic(level.id)}
                            >
                              <FaEdit />
                            </button>
                            <button 
                              className="delete-btn"
                              onClick={() => deleteAcademicLevel(level.id)}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add New Academic Level */}
            <div className="add-new-section">
              <h3>Add New Academic Level</h3>
              <div className="add-new-form">
                <input
                  type="text"
                  placeholder="Academic Level (e.g., Post-Doc)"
                  value={newAcademic.academic_level}
                  onChange={(e) => setNewAcademic({ ...newAcademic, academic_level: e.target.value })}
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Price per Page"
                  value={newAcademic.price_per_page}
                  onChange={(e) => setNewAcademic({ ...newAcademic, price_per_page: e.target.value })}
                />
                <button onClick={addAcademicLevel} className="add-btn">
                  <FaPlus /> Add Level
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deadlines Tab */}
        {activeTab === 'deadlines' && (
          <div className="pricing-section">
            <div className="section-header">
              <h2><FaClock /> Deadline Pricing (Rush Fees)</h2>
              <p>Set rush fee percentages for different deadline ranges</p>
              <button 
                className="save-tab-btn"
                onClick={saveDeadlineHandler}
                disabled={saving}
              >
                {saving ? <FaSpinner className="spinning" /> : <FaSave />}
                Save Deadlines
              </button>
            </div>

            <div className="pricing-table">
              <table>
                <thead>
                  <tr>
                    <th>Sort</th>
                    <th>Display Name</th>
                    <th>Deadline Range</th>
                    <th>Rush Fee (%)</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deadlines.map((deadline, index) => (
                    <tr key={deadline.id}>
                      <td className="sort-controls">
                        <button 
                          className="sort-btn"
                          onClick={() => moveItemUp(deadlines, setDeadlines, index)}
                          disabled={index === 0}
                        >
                          <FaArrowUp />
                        </button>
                        <button 
                          className="sort-btn"
                          onClick={() => moveItemDown(deadlines, setDeadlines, index)}
                          disabled={index === deadlines.length - 1}
                        >
                          <FaArrowDown />
                        </button>
                      </td>
                      <td>
                        {editingDeadline === deadline.id ? (
                          <input
                            type="text"
                            value={deadline.display_name}
                            onChange={(e) => {
                              const newDeadlines = [...deadlines];
                              newDeadlines[index].display_name = e.target.value;
                              setDeadlines(newDeadlines);
                            }}
                          />
                        ) : (
                          deadline.display_name
                        )}
                      </td>
                      <td>
                        {editingDeadline === deadline.id ? (
                          <select
                            value={deadline.range || deadline.deadline_range}
                            onChange={(e) => {
                              const newDeadlines = [...deadlines];
                              newDeadlines[index].range = e.target.value;
                              newDeadlines[index].deadline_range = e.target.value;
                              setDeadlines(newDeadlines);
                            }}
                          >
                            <option value="0-24">0-24 hours</option>
                            <option value="25-72">25-72 hours</option>
                            <option value="73-168">73-168 hours</option>
                            <option value="169-336">169-336 hours</option>
                            <option value="337+">337+ hours</option>
                          </select>
                        ) : (
                          deadline.range || deadline.deadline_range
                        )}
                      </td>
                      <td>
                        {editingDeadline === deadline.id ? (
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={deadline.percentage || deadline.rush_fee_percentage}
                            onChange={(e) => {
                              const newDeadlines = [...deadlines];
                              newDeadlines[index].percentage = parseFloat(e.target.value);
                              newDeadlines[index].rush_fee_percentage = parseFloat(e.target.value);
                              setDeadlines(newDeadlines);
                            }}
                          />
                        ) : (
                          `${deadline.percentage || deadline.rush_fee_percentage}%`
                        )}
                      </td>
                      <td>
                        <button 
                          className={`status-toggle ${deadline.is_active ? 'active' : 'inactive'}`}
                          onClick={() => toggleDeadlineActive(deadline.id)}
                        >
                          {deadline.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </button>
                      </td>
                      <td className="action-btns">
                        {editingDeadline === deadline.id ? (
                          <>
                            <button 
                              className="save-btn"
                              onClick={() => {
                                saveDeadlineHandler();
                                setEditingDeadline(null);
                              }}
                            >
                              <FaCheck />
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={() => setEditingDeadline(null)}
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              className="edit-btn"
                              onClick={() => setEditingDeadline(deadline.id)}
                            >
                              <FaEdit />
                            </button>
                            <button 
                              className="delete-btn"
                              onClick={() => deleteDeadline(deadline.id)}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add New Deadline */}
            <div className="add-new-section">
              <h3>Add New Deadline Tier</h3>
              <div className="add-new-form">
                <input
                  type="text"
                  placeholder="Display Name (e.g., Urgent)"
                  value={newDeadline.display_name}
                  onChange={(e) => setNewDeadline({ ...newDeadline, display_name: e.target.value })}
                />
                <select
                  value={newDeadline.deadline_range}
                  onChange={(e) => setNewDeadline({ ...newDeadline, deadline_range: e.target.value })}
                >
                  <option value="">Select Range</option>
                  <option value="0-24">0-24 hours</option>
                  <option value="25-72">25-72 hours</option>
                  <option value="73-168">73-168 hours</option>
                  <option value="169-336">169-336 hours</option>
                  <option value="337+">337+ hours</option>
                </select>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="Rush Fee %"
                  value={newDeadline.rush_fee_percentage}
                  onChange={(e) => setNewDeadline({ ...newDeadline, rush_fee_percentage: e.target.value })}
                />
                <button onClick={addDeadline} className="add-btn">
                  <FaPlus /> Add Deadline
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Additional Services Tab */}
        {activeTab === 'services' && (
          <div className="pricing-section">
            <div className="section-header">
              <h2><FaStar /> Additional Services</h2>
              <p>Configure additional services and their pricing</p>
              <button 
                className="save-tab-btn"
                onClick={saveServiceHandler}
                disabled={saving}
              >
                {saving ? <FaSpinner className="spinning" /> : <FaSave />}
                Save Services
              </button>
            </div>

            <div className="pricing-table">
              <table>
                <thead>
                  <tr>
                    <th>Sort</th>
                    <th>Service Type</th>
                    <th>Display Name</th>
                    <th>Description</th>
                    <th>Fee Type</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {additionalServices.map((service, index) => (
                    <tr key={service.id}>
                      <td className="sort-controls">
                        <button 
                          className="sort-btn"
                          onClick={() => moveItemUp(additionalServices, setAdditionalServices, index)}
                          disabled={index === 0}
                        >
                          <FaArrowUp />
                        </button>
                        <button 
                          className="sort-btn"
                          onClick={() => moveItemDown(additionalServices, setAdditionalServices, index)}
                          disabled={index === additionalServices.length - 1}
                        >
                          <FaArrowDown />
                        </button>
                      </td>
                      <td>
                        {editingService === service.id ? (
                          <select
                            value={service.type}
                            onChange={(e) => {
                              const newServices = [...additionalServices];
                              newServices[index].type = e.target.value;
                              setAdditionalServices(newServices);
                            }}
                          >
                            <option value="PLAGIARISM">Plagiarism Report</option>
                            <option value="EXECUTIVE_SUMMARY">Executive Summary</option>
                            <option value="EXPERT_WRITER">Expert Writer</option>
                            <option value="PROOFREADING">Proofreading</option>
                            <option value="FORMATTING">Formatting</option>
                            <option value="CHARTS">Charts and Graphs</option>
                            <option value="ABSTRACT">Abstract Page</option>
                            <option value="OUTLINE">Outline</option>
                            <option value="REFERENCES">References Page</option>
                          </select>
                        ) : (
                          service.type
                        )}
                      </td>
                      <td>
                        {editingService === service.id ? (
                          <input
                            type="text"
                            value={service.name}
                            onChange={(e) => {
                              const newServices = [...additionalServices];
                              newServices[index].name = e.target.value;
                              setAdditionalServices(newServices);
                            }}
                          />
                        ) : (
                          service.name
                        )}
                      </td>
                      <td>
                        {editingService === service.id ? (
                          <input
                            type="text"
                            value={service.description}
                            onChange={(e) => {
                              const newServices = [...additionalServices];
                              newServices[index].description = e.target.value;
                              setAdditionalServices(newServices);
                            }}
                          />
                        ) : (
                          service.description
                        )}
                      </td>
                      <td>
                        {editingService === service.id ? (
                          <select
                            value={service.fee_type}
                            onChange={(e) => {
                              const newServices = [...additionalServices];
                              newServices[index].fee_type = e.target.value;
                              setAdditionalServices(newServices);
                            }}
                          >
                            <option value="FIXED">Fixed Price</option>
                            <option value="PER_PAGE">Per Page</option>
                            <option value="PERCENTAGE">Percentage</option>
                          </select>
                        ) : (
                          service.fee_type
                        )}
                      </td>
                      <td>
                        {editingService === service.id ? (
                          <>
                            {service.fee_type === 'FIXED' && (
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Fixed Price"
                                value={service.price || ''}
                                onChange={(e) => {
                                  const newServices = [...additionalServices];
                                  newServices[index].price = parseFloat(e.target.value);
                                  setAdditionalServices(newServices);
                                }}
                              />
                            )}
                            {service.fee_type === 'PER_PAGE' && (
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Price per Page"
                                value={service.price_per_page || ''}
                                onChange={(e) => {
                                  const newServices = [...additionalServices];
                                  newServices[index].price_per_page = parseFloat(e.target.value);
                                  setAdditionalServices(newServices);
                                }}
                              />
                            )}
                            {service.fee_type === 'PERCENTAGE' && (
                              <input
                                type="number"
                                step="0.1"
                                placeholder="Percentage"
                                value={service.percentage || ''}
                                onChange={(e) => {
                                  const newServices = [...additionalServices];
                                  newServices[index].percentage = parseFloat(e.target.value);
                                  setAdditionalServices(newServices);
                                }}
                              />
                            )}
                          </>
                        ) : (
                          getServicePriceDisplay(service)
                        )}
                      </td>
                      <td>
                        <button 
                          className={`status-toggle ${service.is_active ? 'active' : 'inactive'}`}
                          onClick={() => toggleServiceActive(service.id)}
                        >
                          {service.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </button>
                      </td>
                      <td className="action-btns">
                        {editingService === service.id ? (
                          <>
                            <button 
                              className="save-btn"
                              onClick={() => {
                                saveServiceHandler();
                                setEditingService(null);
                              }}
                            >
                              <FaCheck />
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={() => setEditingService(null)}
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              className="edit-btn"
                              onClick={() => setEditingService(service.id)}
                            >
                              <FaEdit />
                            </button>
                            <button 
                              className="delete-btn"
                              onClick={() => deleteService(service.id)}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add New Service */}
            <div className="add-new-section">
              <h3>Add New Service</h3>
              <div className="add-new-form grid-2">
                <select
                  value={newService.service_type}
                  onChange={(e) => setNewService({ ...newService, service_type: e.target.value })}
                >
                  <option value="">Select Service Type</option>
                  <option value="PLAGIARISM">Plagiarism Report</option>
                  <option value="EXECUTIVE_SUMMARY">Executive Summary</option>
                  <option value="EXPERT_WRITER">Expert Writer</option>
                  <option value="PROOFREADING">Proofreading</option>
                  <option value="FORMATTING">Formatting</option>
                  <option value="CHARTS">Charts and Graphs</option>
                  <option value="ABSTRACT">Abstract Page</option>
                  <option value="OUTLINE">Outline</option>
                  <option value="REFERENCES">References Page</option>
                </select>
                <input
                  type="text"
                  placeholder="Display Name"
                  value={newService.display_name}
                  onChange={(e) => setNewService({ ...newService, display_name: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="Description"
                  value={newService.description}
                  onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                  className="full-width"
                />
                <select
                  value={newService.fee_type}
                  onChange={(e) => setNewService({ ...newService, fee_type: e.target.value })}
                >
                  <option value="FIXED">Fixed Price</option>
                  <option value="PER_PAGE">Per Page</option>
                  <option value="PERCENTAGE">Percentage</option>
                </select>
                {newService.fee_type === 'FIXED' && (
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Fixed Price"
                    value={newService.fixed_price}
                    onChange={(e) => setNewService({ ...newService, fixed_price: e.target.value })}
                  />
                )}
                {newService.fee_type === 'PER_PAGE' && (
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Price per Page"
                    value={newService.per_page_price}
                    onChange={(e) => setNewService({ ...newService, per_page_price: e.target.value })}
                  />
                )}
                {newService.fee_type === 'PERCENTAGE' && (
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Percentage"
                    value={newService.percentage}
                    onChange={(e) => setNewService({ ...newService, percentage: e.target.value })}
                  />
                )}
                <button onClick={addService} className="add-btn full-width">
                  <FaPlus /> Add Service
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Page Discounts Tab */}
        {activeTab === 'discounts' && (
          <div className="pricing-section">
            <div className="section-header">
              <h2><FaPercentage /> Page Count Discounts/Premiums</h2>
              <p>Set volume discounts or premiums based on page count</p>
              <button 
                className="save-tab-btn"
                onClick={saveDiscountHandler}
                disabled={saving}
              >
                {saving ? <FaSpinner className="spinning" /> : <FaSave />}
                Save Discounts
              </button>
            </div>

            <div className="pricing-table">
              <table>
                <thead>
                  <tr>
                    <th>Page Range</th>
                    <th>Type</th>
                    <th>Value (%)</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageDiscounts.map((discount, index) => (
                    <tr key={discount.id}>
                      <td>
                        {editingDiscount === discount.id ? (
                          <div className="range-inputs">
                            <input
                              type="number"
                              min="1"
                              placeholder="Min"
                              value={discount.min_pages}
                              onChange={(e) => {
                                const newDiscounts = [...pageDiscounts];
                                newDiscounts[index].min_pages = parseInt(e.target.value);
                                setPageDiscounts(newDiscounts);
                              }}
                            />
                            <span>-</span>
                            <input
                              type="number"
                              min="1"
                              placeholder="Max (optional)"
                              value={discount.max_pages || ''}
                              onChange={(e) => {
                                const newDiscounts = [...pageDiscounts];
                                newDiscounts[index].max_pages = e.target.value ? parseInt(e.target.value) : null;
                                setPageDiscounts(newDiscounts);
                              }}
                            />
                          </div>
                        ) : (
                          discount.min_pages + (discount.max_pages ? `-${discount.max_pages}` : '+') + ' pages'
                        )}
                      </td>
                      <td>
                        {editingDiscount === discount.id ? (
                          <select
                            value={discount.modifier_type}
                            onChange={(e) => {
                              const newDiscounts = [...pageDiscounts];
                              newDiscounts[index].modifier_type = e.target.value;
                              setPageDiscounts(newDiscounts);
                            }}
                          >
                            <option value="DISCOUNT">Discount</option>
                            <option value="PREMIUM">Premium</option>
                          </select>
                        ) : (
                          discount.modifier_type
                        )}
                      </td>
                      <td>
                        {editingDiscount === discount.id ? (
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={discount.modifier_value}
                            onChange={(e) => {
                              const newDiscounts = [...pageDiscounts];
                              newDiscounts[index].modifier_value = parseFloat(e.target.value);
                              setPageDiscounts(newDiscounts);
                            }}
                          />
                        ) : (
                          `${discount.modifier_value}%`
                        )}
                      </td>
                      <td>
                        <button 
                          className={`status-toggle ${discount.is_active ? 'active' : 'inactive'}`}
                          onClick={() => toggleDiscountActive(discount.id)}
                        >
                          {discount.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </button>
                      </td>
                      <td className="action-btns">
                        {editingDiscount === discount.id ? (
                          <>
                            <button 
                              className="save-btn"
                              onClick={() => {
                                saveDiscountHandler();
                                setEditingDiscount(null);
                              }}
                            >
                              <FaCheck />
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={() => setEditingDiscount(null)}
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              className="edit-btn"
                              onClick={() => setEditingDiscount(discount.id)}
                            >
                              <FaEdit />
                            </button>
                            <button 
                              className="delete-btn"
                              onClick={() => deleteDiscount(discount.id)}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add New Discount */}
            <div className="add-new-section">
              <h3>Add New Page Discount/Premium</h3>
              <div className="add-new-form">
                <input
                  type="number"
                  min="1"
                  placeholder="Min Pages"
                  value={newDiscount.min_pages}
                  onChange={(e) => setNewDiscount({ ...newDiscount, min_pages: e.target.value })}
                />
                <input
                  type="number"
                  min="1"
                  placeholder="Max Pages (optional)"
                  value={newDiscount.max_pages}
                  onChange={(e) => setNewDiscount({ ...newDiscount, max_pages: e.target.value })}
                />
                <select
                  value={newDiscount.modifier_type}
                  onChange={(e) => setNewDiscount({ ...newDiscount, modifier_type: e.target.value })}
                >
                  <option value="DISCOUNT">Discount</option>
                  <option value="PREMIUM">Premium</option>
                </select>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="Value %"
                  value={newDiscount.modifier_value}
                  onChange={(e) => setNewDiscount({ ...newDiscount, modifier_value: e.target.value })}
                />
                <button onClick={addDiscount} className="add-btn">
                  <FaPlus /> Add Rule
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subject Multipliers Tab */}
        {activeTab === 'subjects' && (
          <div className="pricing-section">
            <div className="section-header">
              <h2><FaBook /> Subject Price Multipliers</h2>
              <p>Set price multipliers for specific subjects (1.0 = standard price)</p>
              <button 
                className="save-tab-btn"
                onClick={saveSubjectHandler}
                disabled={saving}
              >
                {saving ? <FaSpinner className="spinning" /> : <FaSave />}
                Save Subjects
              </button>
            </div>

            <div className="pricing-table">
              <table>
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Multiplier</th>
                    <th>Effect</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subjectMultipliers.map((subject, index) => (
                    <tr key={subject.id}>
                      <td>
                        {editingSubject === subject.id ? (
                          <input
                            type="text"
                            value={subject.subject}
                            onChange={(e) => {
                              const newSubjects = [...subjectMultipliers];
                              newSubjects[index].subject = e.target.value;
                              setSubjectMultipliers(newSubjects);
                            }}
                          />
                        ) : (
                          subject.subject
                        )}
                      </td>
                      <td>
                        {editingSubject === subject.id ? (
                          <input
                            type="number"
                            step="0.05"
                            min="0.5"
                            max="3"
                            value={subject.price_multiplier}
                            onChange={(e) => {
                              const newSubjects = [...subjectMultipliers];
                              newSubjects[index].price_multiplier = parseFloat(e.target.value);
                              setSubjectMultipliers(newSubjects);
                            }}
                          />
                        ) : (
                          `x${subject.price_multiplier}`
                        )}
                      </td>
                      <td>
                        {subject.price_multiplier > 1 ? (
                          <span className="premium-badge">+{((subject.price_multiplier - 1) * 100).toFixed(0)}% Premium</span>
                        ) : subject.price_multiplier < 1 ? (
                          <span className="discount-badge">-{((1 - subject.price_multiplier) * 100).toFixed(0)}% Discount</span>
                        ) : (
                          <span className="standard-badge">Standard</span>
                        )}
                      </td>
                      <td>
                        <button 
                          className={`status-toggle ${subject.is_active ? 'active' : 'inactive'}`}
                          onClick={() => toggleSubjectActive(subject.id)}
                        >
                          {subject.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </button>
                      </td>
                      <td className="action-btns">
                        {editingSubject === subject.id ? (
                          <>
                            <button 
                              className="save-btn"
                              onClick={() => {
                                saveSubjectHandler();
                                setEditingSubject(null);
                              }}
                            >
                              <FaCheck />
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={() => setEditingSubject(null)}
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              className="edit-btn"
                              onClick={() => setEditingSubject(subject.id)}
                            >
                              <FaEdit />
                            </button>
                            <button 
                              className="delete-btn"
                              onClick={() => deleteSubject(subject.id)}
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add New Subject Multiplier */}
            <div className="add-new-section">
              <h3>Add New Subject Multiplier</h3>
              <div className="add-new-form">
                <input
                  type="text"
                  placeholder="Subject Name"
                  value={newSubject.subject}
                  onChange={(e) => setNewSubject({ ...newSubject, subject: e.target.value })}
                />
                <input
                  type="number"
                  step="0.05"
                  min="0.5"
                  max="3"
                  placeholder="Multiplier (e.g., 1.2)"
                  value={newSubject.price_multiplier}
                  onChange={(e) => setNewSubject({ ...newSubject, price_multiplier: e.target.value })}
                />
                <button onClick={addSubject} className="add-btn">
                  <FaPlus /> Add Subject
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Site Settings Tab */}
        {activeTab === 'settings' && (
          <div className="pricing-section">
            <div className="section-header">
              <h2><FaCog /> Site Settings</h2>
              <p>Configure global site settings</p>
              <button 
                className="save-tab-btn"
                onClick={saveSiteSettingsHandler}
                disabled={saving}
              >
                {saving ? <FaSpinner className="spinning" /> : <FaSave />}
                Save Settings
              </button>
            </div>

            <div className="settings-grid">
              <div className="setting-item">
                <label>
                  <FaDollarSign /> Currency
                </label>
                <select
                  value={siteSettings.currency}
                  onChange={(e) => setSiteSettings({ ...siteSettings, currency: e.target.value })}
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="KES">KES (KSh)</option>
                </select>
              </div>

              <div className="setting-item">
                <label>
                  <FaDollarSign /> Minimum Order Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={siteSettings.minimum_order}
                  onChange={(e) => setSiteSettings({ ...siteSettings, minimum_order: parseFloat(e.target.value) })}
                />
              </div>

              <div className="setting-item">
                <label>
                  <FaPercentage /> Tax Rate (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={siteSettings.tax_rate}
                  onChange={(e) => setSiteSettings({ ...siteSettings, tax_rate: parseFloat(e.target.value) })}
                />
              </div>

              <div className="setting-item toggle">
                <label>
                  <FaClock /> Enable Rush Fees
                </label>
                <button 
                  className={`toggle-btn ${siteSettings.enable_rush_fees ? 'active' : 'inactive'}`}
                  onClick={() => setSiteSettings({ ...siteSettings, enable_rush_fees: !siteSettings.enable_rush_fees })}
                >
                  {siteSettings.enable_rush_fees ? <FaToggleOn /> : <FaToggleOff />}
                </button>
              </div>

              <div className="setting-item toggle">
                <label>
                  <FaStar /> Enable Additional Services
                </label>
                <button 
                  className={`toggle-btn ${siteSettings.enable_additional_services ? 'active' : 'inactive'}`}
                  onClick={() => setSiteSettings({ ...siteSettings, enable_additional_services: !siteSettings.enable_additional_services })}
                >
                  {siteSettings.enable_additional_services ? <FaToggleOn /> : <FaToggleOff />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Global Save Button */}
      <div className="global-save-bar">
        <button 
          className="global-save-btn"
          onClick={saveAllHandler}
          disabled={saving}
        >
          {saving ? <FaSpinner className="spinning" /> : <FaSave />}
          Save All Changes
        </button>
      </div>
    </div>
  );
};

export default AdminPricingPage;